import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import { Music, ShoppingBag, ExternalLink, Instagram, Youtube, Mail, Mic2, Theater, Users, GraduationCap, Film, Headphones, Camera, Globe, Palette, Briefcase, Shirt, Handshake, MessageSquare, ChevronDown, Zap, MapPin, Calendar, Clock } from 'lucide-react';
import { contentAPI, mediaAPI } from '../services/api';
import { NEWSLETTER_FORM_URL } from '../data/ribbonData';
import { NewsletterPopup } from '../components/NewsletterPopup';
import { ThunderTickets } from '../components/ThunderTickets';

// Threads of Thunder Flyer Image
const THUNDER_FLYER_URL = 'https://customer-assets-cm19k8pv.emergentagent.net/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/noycoyhm_THREADS%20OF%20THUNDER%20PT2.png';

// Application Links
const APPLICATION_LINKS = {
  designer: 'https://form.jotform.com/262431201737146',
  model: 'https://form.jotform.com/262426810967161',
  vendor: 'https://form.jotform.com/262726803516156',
  beads: 'https://form.jotform.com/262726527118157'
};

const Home = () => {
  // State for all data - with default values so page renders instantly
  const [heroData, setHeroData] = useState({
    title: "FINEST FEATHERS",
    subtitle: "Indigenous Fashion & Culture",
    description: "Celebrating Native American heritage through fashion, music, and community."
  });
  const [missionData, setMissionData] = useState(null);
  const [musicData, setMusicData] = useState(null);
  const [ctaData, setCtaData] = useState(null);
  const [storyData, setStoryData] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [pastEventsMedia, setPastEventsMedia] = useState([]);
  const [showAllServices, setShowAllServices] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('jackets');
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [bannerImages, setBannerImages] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch data in background without blocking render
        
        // Fetch products via backend proxy (avoids CORS issues on deployed site)
        let shopifyProducts = [];
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
          const shopifyRes = await fetch(`${API_URL}/api/shopify/products`);
          const shopifyData = await shopifyRes.json();
          shopifyProducts = shopifyData.products || [];
        } catch (e) {
          console.error('Error fetching products:', e);
        }
        
        // Fetch other data from API
        const [content, pastMedia] = await Promise.all([
          contentAPI.getAllSections().catch(() => ({})),
          mediaAPI.getByCategory('past_events').catch(() => [])
        ]);
        
        // Fetch new sections
        const story = await contentAPI.getSection('story').catch(() => null);
        const processSection = await contentAPI.getSection('process').catch(() => null);
        const banners = await mediaAPI.getByCategory('hero').catch(() => []);
        
        // Update state with fetched data (page already visible)
        if (content.hero) setHeroData(content.hero);
        if (content.mission) setMissionData(content.mission);
        if (content.music) setMusicData(content.music);
        if (content.cta) setCtaData(content.cta);
        if (story) setStoryData(story);
        if (processSection) setProcessData(processSection);
        setFeaturedProducts(shopifyProducts.slice(0, 8));
        setAllProducts(shopifyProducts);
        setPastEventsMedia(pastMedia);
        setBannerImages(banners.filter(b => b.title?.includes('Banner') || false));
        
      } catch (err) {
        console.error('Error fetching data:', err);
        // Don't show error - page still works with defaults
      }
    };
    
    fetchData();
  }, []);

  // Removed loading spinner - page renders immediately now

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Shared Header Component */}
      <Header />
      <NewsletterPopup />

      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video or Image */}
        <div className="absolute inset-0 z-0">
          {heroData?.video_url ? (
            <>
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={heroData.video_url} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
            </>
          ) : (
            <>
              <img
                src={heroData?.image_url || "https://images.unsplash.com/photo-1558769132-cb1aea808c16?w=1920&q=80"}
                alt="Hero Background"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
            </>
          )}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl">
          <h1 className="text-5xl md:text-7xl mb-6 tracking-wider leading-tight font-bold text-white heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif' }}>
            FINEST FEATHERS
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-4 font-semibold tracking-wide">{heroData?.subtitle || 'Fashion • Film • Music'}</p>
          <p className="text-base md:text-lg text-orange-400 font-semibold mb-8 max-w-2xl mx-auto">
            {heroData?.metadata?.tagline || heroData?.description || 'Amplifying Indigenous Voices in Creative Spaces'}
          </p>
          
          <div className="flex gap-4 justify-center items-center flex-wrap">
            <Button
              onClick={() => window.open(process.env.REACT_APP_SHOPIFY_URL || 'https://shop.finestfeathers.com/collections/all?page=2', '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-6 text-lg flex items-center gap-2 btn-pulse-glow"
            >
              <ShoppingBag className="w-5 h-5" />
              Shop Now
            </Button>
            <Button
              onClick={() => window.open(process.env.REACT_APP_PAYPAL_DONATION_URL || 'https://www.paypal.com/ncp/payment/M7ZXP8B9GRVSQ', '_blank')}
              className="bg-white hover:bg-gray-200 text-black font-bold px-8 py-6 text-lg flex items-center gap-2 btn-orange-glow"
            >
              Donate
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-orange-500 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-orange-500 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* THREADS OF THUNDER 2026 - Fashion Show Announcement */}
      <section className="thunder-section py-16 sm:py-20 md:py-24 px-4 sm:px-6" data-testid="threads-of-thunder-section">
        {/* Lightning Effects */}
        <div className="lightning-left hidden md:block"></div>
        <div className="lightning-right hidden md:block"></div>
        <div className="lightning-branch-left hidden lg:block"></div>
        <div className="lightning-branch-right hidden lg:block"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Flyer Image */}
            <div className="order-2 lg:order-1">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 rounded-2xl opacity-40 blur-2xl group-hover:opacity-60 transition-opacity duration-500"></div>
              <img
                src={THUNDER_FLYER_URL}
                alt="Threads of Thunder 2026 Fashion Show"
                className="relative w-full rounded-2xl shadow-2xl border-2 border-yellow-500/30 yellow-glow"
                data-testid="thunder-flyer-image"
              />
            </div>
            <ThunderTickets />
            </div>

            {/* Event Details & Application Form */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm font-bold uppercase tracking-wider">4th Annual Event</span>
              </div>

              {/* Title */}
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-4 heading-oswald yellow-glow-text" style={{ fontFamily: 'Oswald, sans-serif', color: '#ffd700' }}>
                THREADS OF<br />
                <span className="text-white">THUNDER</span> 2026
              </h2>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-xl mx-auto lg:mx-0">
                Indigenous Fashion Show bringing together fashion, art, culture, creativity & community!
              </p>

              {/* Event Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-xl p-4 text-center">
                  <Calendar className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-white font-bold">Nov 20, 2026</p>
                  <p className="text-gray-400 text-sm">Friday</p>
                </div>
                <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-white font-bold">5PM - 8PM</p>
                  <p className="text-gray-400 text-sm">Doors Open</p>
                </div>
                <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-xl p-4 text-center">
                  <MapPin className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-white font-bold text-sm">Santa Rosa</p>
                  <p className="text-gray-400 text-sm">Fairgrounds</p>
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-zinc-900/60 border border-yellow-500/40 rounded-2xl p-6 mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4 heading-oswald" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  APPLY NOW
                </h3>
                <p className="text-gray-300 mb-4">
                  We are calling all <span className="text-yellow-400 font-semibold">Models</span>, <span className="text-yellow-400 font-semibold">Designers</span>, <span className="text-yellow-400 font-semibold">Vendors</span> & <span className="text-yellow-400 font-semibold">Battle of the Beads Artists</span>
                </p>

                {/* Application Dropdown */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <select
                    value={selectedApplication}
                    onChange={(e) => setSelectedApplication(e.target.value)}
                    className="flex-1 bg-zinc-800 border-2 border-yellow-500/50 text-white rounded-xl px-4 py-3 text-base font-medium focus:outline-none focus:border-yellow-400 cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffd700'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    data-testid="application-dropdown"
                  >
                    <option value="">Select Application Type</option>
                    <option value="designer">Designer Application</option>
                    <option value="model">Model Application</option>
                    <option value="vendor">Vendor Application</option>
                    <option value="beads">Battle of the Beads Application</option>
                  </select>
                  <Button
                    onClick={() => {
                      if (selectedApplication && APPLICATION_LINKS[selectedApplication]) {
                        window.open(APPLICATION_LINKS[selectedApplication], '_blank');
                      }
                    }}
                    disabled={!selectedApplication}
                    className={`px-8 py-3 font-bold text-base rounded-xl transition-all ${
                      selectedApplication 
                        ? 'bg-yellow-500 hover:bg-yellow-400 text-black btn-yellow-glow' 
                        : 'bg-zinc-700 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="apply-now-btn"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    Apply Now
                  </Button>
                </div>
              </div>

              {/* Contact Info */}
              <p className="text-gray-400 text-sm">
                Questions? Email: <a href="mailto:FinestFeathersProductions@gmail.com" className="text-yellow-400 hover:text-yellow-300 underline">FinestFeathersProductions@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission - Web Banner */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/a2sa4q14_Threads%20of%20Thunder%20Fashion%20Show_Orion%20Sky%20Studios-617.jpg"
            alt="Our Mission"
            className="w-full h-full object-cover object-left"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
        </div>
        <div className="relative z-10 container mx-auto h-full flex items-center px-6">
          <div className="max-w-3xl ml-0 md:ml-16">
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif' }}>
              OUR MISSION
            </h2>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
              {missionData?.description || 'Finest Feathers exists to uplift Indigenous creatives, empower youth, and amplify cultural voices through fashion, storytelling, and community-driven experiences.'}
            </p>
          </div>
        </div>
      </section>

      {/* Join EntrepreNatives Section - moved up under Our Mission */}
      <section id="entreprenatives" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden bg-gradient-to-b from-black via-zinc-950 to-black">
        {/* Animated background glows */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-40 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-orange-500/10 blur-3xl en-float-up"></div>
          <div className="absolute -bottom-40 -right-40 w-[350px] sm:w-[600px] h-[350px] sm:h-[600px] rounded-full bg-amber-500/10 blur-3xl en-float-down"></div>
        </div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14 items-center">

            {/* LEFT: Pitch */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-3 bg-orange-500/15 border border-orange-500/40 rounded-full px-4 sm:px-5 py-2 mb-6">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-orange-400 en-ping"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500"></span>
                </span>
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px] sm:text-xs">Now Live · Indigenous Marketplace</span>
              </div>

              <h2 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.05] break-words" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.02em' }}>
                <span className="text-white block sm:inline">JOIN </span>
                <span className="en-gradient-text block sm:inline">ENTREPRENATIVES</span>
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 leading-relaxed max-w-xl mx-auto lg:mx-0">
                The first <span className="text-orange-400 font-semibold">Indigenous business marketplace</span>. Discover, hire, and support verified Native vendors — all protected by secure escrow payments.
              </p>

              <p className="text-sm sm:text-base text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Build your brand. Get booked. Circulate wealth across Turtle Island.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-10 max-w-md mx-auto lg:mx-0">
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-black en-count-pulse" style={{ fontFamily: 'Oswald, sans-serif' }}>100%</div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-gray-500 mt-1">Verified</div>
                </div>
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-black en-count-pulse" style={{ fontFamily: 'Oswald, sans-serif', animationDelay: '0.4s' }}>46+</div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-gray-500 mt-1">Industries</div>
                </div>
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-black en-count-pulse" style={{ fontFamily: 'Oswald, sans-serif', animationDelay: '0.8s' }}>332+</div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-gray-500 mt-1">Categories</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => window.open('https://entreprenatives.com/auth?mode=register&role=vendor', '_blank')}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-black px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full en-pulse-glow flex items-center justify-center gap-2 w-full sm:w-auto"
                  data-testid="entreprenatives-vendor-btn"
                >
                  Become a Vendor
                  <ExternalLink className="w-5 h-5" />
                </Button>
                <Button
                  onClick={() => window.open('https://entreprenatives.com/auth?mode=register&role=consumer', '_blank')}
                  variant="outline"
                  className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full bg-transparent w-full sm:w-auto"
                  data-testid="entreprenatives-hire-btn"
                >
                  Hire a Vendor — Free
                </Button>
              </div>

              <p className="text-gray-500 text-[11px] sm:text-xs mt-5 italic px-2 lg:px-0">
                Indigenous-owned · Native American · First Nations · Alaska Native · Hawaiian · Métis · Inuit
              </p>
            </div>

            {/* RIGHT: Animated browser mockup */}
            <div className="relative max-w-md mx-auto w-full lg:max-w-none">
              {/* Floating accent rings */}
              <div className="pointer-events-none absolute -top-6 -right-6 w-20 sm:w-32 h-20 sm:h-32 border-2 border-orange-500/30 rounded-full en-spin-slow"></div>
              <div className="pointer-events-none absolute -bottom-6 -left-6 w-16 sm:w-24 h-16 sm:h-24 border-2 border-amber-500/30 rounded-full en-spin-slow" style={{ animationDirection: 'reverse' }}></div>

              <a
                href="https://entreprenatives.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="entreprenatives-preview-link"
                className="block relative group"
              >
                <div className="absolute -inset-3 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl opacity-40 blur-2xl group-hover:opacity-70 transition-opacity duration-500"></div>

                <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden transform group-hover:scale-[1.02] transition-transform duration-500">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-zinc-800 border-b border-zinc-700">
                    <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></span>
                    <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></span>
                    <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></span>
                    <div className="flex-1 mx-2 sm:mx-4 px-2 sm:px-4 py-1 sm:py-1.5 bg-zinc-900 rounded-md text-[10px] sm:text-xs text-gray-400 font-mono truncate flex items-center gap-2">
                      <span className="text-green-400">●</span> entreprenatives.com
                    </div>
                  </div>

                  {/* Page content - animated mock of EN homepage */}
                  <div className="relative h-[380px] sm:h-[460px] bg-gradient-to-br from-stone-900 via-zinc-900 to-black overflow-hidden">
                    {/* Decorative grid */}
                    <div
                      className="absolute inset-0 opacity-20"
                      style={{
                        backgroundImage: 'linear-gradient(rgba(251,146,60,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.15) 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                      }}
                    ></div>

                    {/* Hero content */}
                    <div className="relative z-10 px-4 sm:px-8 pt-8 sm:pt-10 pb-6 text-center">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-orange-400 mb-2 sm:mb-3">EntrepreNatives</p>
                      <h3 className="font-black leading-tight mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        <span className="block text-xl sm:text-2xl md:text-3xl en-gradient-text">SUPPORT INDIGENOUS</span>
                        <span className="block text-xl sm:text-2xl md:text-3xl text-white">BUSINESSES</span>
                      </h3>
                      <p className="text-[11px] sm:text-xs text-gray-400 max-w-xs mx-auto mb-4 sm:mb-5">
                        Connect with verified vendors. Secure escrow. Build legacy.
                      </p>
                      <div className="inline-flex items-center gap-2 bg-orange-500 text-black text-[11px] sm:text-xs font-black px-4 sm:px-5 py-2 sm:py-2.5 rounded-full en-pulse-glow">
                        Start Your Journey →
                      </div>
                    </div>

                    {/* Floating category pills marquee */}
                    <div className="absolute bottom-16 sm:bottom-20 left-0 right-0 overflow-hidden">
                      <div className="flex gap-2 sm:gap-3 en-marquee-track w-max">
                        {[...Array(2)].map((_, dup) => (
                          <React.Fragment key={dup}>
                            {['Fashion', 'Music', 'Food', 'Art', 'Tech', 'Beauty', 'Wellness', 'Crafts', 'Media', 'Consulting'].map((cat) => (
                              <span
                                key={`${dup}-${cat}`}
                                className="shrink-0 bg-zinc-800/80 border border-orange-500/30 text-orange-300 text-[10px] sm:text-xs font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full backdrop-blur-sm"
                              >
                                {cat}
                              </span>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Stat strip */}
                    <div className="absolute bottom-0 left-0 right-0 grid grid-cols-3 border-t border-zinc-800 bg-black/60 backdrop-blur-sm">
                      <div className="py-2 sm:py-3 text-center border-r border-zinc-800">
                        <div className="text-orange-400 text-xs sm:text-sm font-black">100%</div>
                        <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-gray-500">Verified</div>
                      </div>
                      <div className="py-2 sm:py-3 text-center border-r border-zinc-800">
                        <div className="text-orange-400 text-xs sm:text-sm font-black">SAFE</div>
                        <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-gray-500">Escrow</div>
                      </div>
                      <div className="py-2 sm:py-3 text-center">
                        <div className="text-orange-400 text-xs sm:text-sm font-black">46+</div>
                        <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-gray-500">Categories</div>
                      </div>
                    </div>

                    {/* Floating sparkle badges */}
                    <div className="absolute top-4 sm:top-6 right-4 sm:right-6 en-float-up">
                      <div className="bg-orange-500/20 border border-orange-500/50 backdrop-blur-sm rounded-full px-2 sm:px-3 py-1 text-[9px] sm:text-[10px] font-bold text-orange-300 uppercase tracking-wider">
                        Verified ✓
                      </div>
                    </div>
                    <div className="absolute top-16 sm:top-24 left-3 sm:left-4 en-float-down">
                      <div className="bg-amber-500/20 border border-amber-500/50 backdrop-blur-sm rounded-full px-2 sm:px-3 py-1 text-[9px] sm:text-[10px] font-bold text-amber-300 uppercase tracking-wider">
                        Escrow Secure
                      </div>
                    </div>
                  </div>
                </div>

                {/* Click prompt */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-orange-500 text-black text-[10px] sm:text-xs font-black uppercase tracking-wider px-4 sm:px-5 py-1.5 sm:py-2 rounded-full shadow-lg en-pulse-glow whitespace-nowrap">
                  Click to Visit Live Site
                </div>
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* 3-Step Process Section - Hartley Style */}
      <section id="about" className="py-24 px-6 bg-black ambient-orange-bg">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">OUR</span> PROCESS
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {processData?.metadata?.steps?.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-20 h-20 rounded-full bg-orange-500 text-black flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                  {step.number}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section - Centered like Our Process */}
      <section id="story" className="py-24 px-6 bg-black ambient-orange-bg">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">OUR</span> STORY
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            
            <p className="text-xl font-semibold text-orange-500 mb-8">
              {storyData?.subtitle || "Jose 'Hands' Lopez IV - Founder"}
            </p>
          </div>

          <div className="flex flex-col items-center gap-12">
            {/* Image */}
            <div className="max-w-md">
              <img
                src={storyData?.image_url || "https://customer-assets.emergentagent.com/job_style-central-65/artifacts/f9k0wocu_Hands%20Headshot%20.JPG"}
                alt={storyData?.subtitle || "Founder"}
                className="w-full rounded-lg shadow-2xl"
              />
              {/* Instagram CTA */}
              <div className="mt-6 text-center">
                <Button
                  onClick={() => window.open(process.env.REACT_APP_INSTAGRAM_FOUNDER_URL || 'https://www.instagram.com/hands707/', '_blank')}
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white font-bold px-8 py-3 rounded-full btn-orange-glow"
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Follow @hands707
                </Button>
              </div>
            </div>

            {/* Story Content */}
            <div className="max-w-3xl text-center">
              <div className="text-gray-300 leading-relaxed space-y-4">
                {storyData?.description?.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {storyData?.metadata?.short_mission && (
                <div className="mt-8 p-6 bg-zinc-900/50 border-l-4 border-orange-500 rounded text-left">
                  <p className="text-lg italic text-gray-200">
                    "{storyData.metadata.short_mission}"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article Section - Voices of Monterey Bay */}
      <section id="featured-article" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-black ambient-orange-bg">
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-orange-500 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-3">As Featured In</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">PRESS</span> COVERAGE
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <a
            href="https://voicesofmontereybay.org/2026/05/14/carmels-rumsen-indians-youth-takeovers-feature-cultural-vitality-and-creativity/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="featured-article-card"
            className="group block relative"
          >
            {/* Glow halo */}
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl sm:rounded-3xl opacity-50 blur-2xl group-hover:opacity-80 transition-opacity duration-500 en-pulse-glow"></div>

            {/* Card */}
            <article className="relative bg-zinc-950 border-2 border-orange-500/60 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-[1.01] transition-transform duration-500">
              <div className="grid md:grid-cols-5 gap-0">

                {/* Image */}
                <div className="md:col-span-2 relative overflow-hidden h-64 sm:h-80 md:h-auto md:min-h-[420px]">
                  <img
                    src="https://voicesofmontereybay.org/wp-content/uploads/2026/05/IndigenousJacket.jpg"
                    alt="Rise of the Red Runway - Carmel's Rumsen Indians Youth Takeover"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/80 via-black/20 to-transparent"></div>

                  {/* Publication badge */}
                  <div className="absolute top-4 left-4 bg-orange-500 text-black text-[10px] sm:text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">
                    Voices of Monterey Bay
                  </div>

                  {/* Article date */}
                  <div className="absolute bottom-4 left-4 text-white/90 text-xs sm:text-sm font-semibold">
                    May 14, 2026 · Features
                  </div>
                </div>

                {/* Content */}
                <div className="md:col-span-3 p-6 sm:p-8 md:p-10 flex flex-col justify-center">

                  {/* Eyebrow */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-[2px] bg-orange-500"></span>
                    <span className="text-orange-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Press Feature</span>
                  </div>

                  {/* Custom headline */}
                  <h3
                    className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-[1.05] heading-oswald"
                    style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.02em' }}
                  >
                    <span className="en-gradient-text">STILL HERE.</span><br />
                    <span className="text-white">STILL THRIVING.</span>
                  </h3>

                  {/* Article actual title */}
                  <p className="text-base sm:text-lg text-gray-200 font-semibold leading-snug mb-5 italic">
                    "Carmel's Rumsen Indians 'youth takeovers' feature cultural vitality and creativity"
                  </p>

                  {/* Pull quote */}
                  <blockquote className="border-l-4 border-orange-500 pl-4 mb-6">
                    <p className="text-sm sm:text-base text-gray-300 italic leading-relaxed">
                      "At one point, we were not allowed to do what's taking place today. So I want to continue with this message that we are still here, not just surviving but thriving."
                    </p>
                    <footer className="text-orange-400 text-xs sm:text-sm font-bold mt-2 not-italic">
                      — Jose "Hands" Lopez, Founder
                    </footer>
                  </blockquote>

                  {/* Byline */}
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b border-zinc-800">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-black font-black text-sm">
                      GS
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold leading-tight">By George B. Sánchez-Tello</p>
                      <p className="text-gray-500 text-xs">Voices of Monterey Bay</p>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2 text-orange-400 group-hover:text-orange-300 font-bold text-base sm:text-lg transition-colors">
                      Read the Full Story
                      <ExternalLink className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                    </span>
                    <span className="hidden sm:inline-block text-gray-600 text-xs font-mono">
                      voicesofmontereybay.org
                    </span>
                  </div>
                </div>

              </div>
            </article>
          </a>
        </div>
      </section>

      {/* Book Us - Web Banner */}
      <section id="book-us" className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/5yig0u6d_Threads%20of%20Thunder%20Fashion%20Show_Orion%20Sky%20Studios-065.jpg"
            alt="Book Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="relative z-10 container mx-auto h-full flex items-center justify-center px-6 text-center">
          <div className="max-w-4xl">
            <h2 className="text-6xl md:text-8xl font-black text-white mb-6 heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.1em' }}>
              BOOK US
            </h2>
            <p className="text-xl md:text-2xl text-orange-500 font-bold mb-4">
              Bring Indigenous creativity to your event
            </p>
            <p className="text-lg text-gray-300 mb-10">
              Book a creative service, performance, production consultation or speaking engagement
            </p>
            <Button
              onClick={() => window.open('https://form.jotform.com/260199128265058', '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-12 py-6 text-xl rounded-full transform hover:scale-105 transition-all shadow-2xl btn-pulse-glow"
            >
              Book Now
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section - EntrepreNatives Style */}
      <section className="py-24 px-6 bg-zinc-900 ambient-orange-bg">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-orange-500 text-lg font-semibold mb-4">Our Services</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Delivering The Finest To NDN Country
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Indigenous creative services across every industry
            </p>
          </div>

          {(() => {
            const services = [
              { icon: Mic2,           title: 'Performances',                   tagline: 'Live • On Stage' },
              { icon: Theater,        title: 'Fashion Show Productions',       tagline: 'Runway' },
              { icon: Users,          title: 'Workshops',                      tagline: 'Hands-On' },
              { icon: GraduationCap,  title: 'Youth Seminars',                 tagline: 'Empowerment' },
              { icon: Film,           title: 'Digital Storytelling',           tagline: 'Film & Story' },
              { icon: Headphones,     title: 'Music Recording',                tagline: 'Studio Sessions' },
              { icon: Camera,         title: 'Photography / Videography',      tagline: 'Capture' },
              { icon: Globe,          title: 'Web Design',                     tagline: 'Websites' },
              { icon: Palette,        title: 'Graphic Design',                 tagline: 'Visual Identity' },
              { icon: Briefcase,      title: 'Brand Consulting',               tagline: 'Strategy' },
              { icon: Shirt,          title: 'Clothing Printing',              tagline: 'Custom Apparel' },
              { icon: Handshake,      title: 'Collaborations',                 tagline: 'Partnerships' },
              { icon: MessageSquare,  title: 'Speaking Gigs',                  tagline: 'Keynotes & Panels' },
            ];
            const visibleCount = 6;
            const visible = showAllServices ? services : services.slice(0, visibleCount);

            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {visible.map(({ icon: Icon, title, tagline }) => (
                    <div
                      key={title}
                      className="group flex items-center gap-4 bg-zinc-950/70 border border-zinc-800 hover:border-orange-500/60 rounded-2xl p-4 md:p-5 transition-all duration-300 hover:bg-zinc-900"
                      data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                    >
                      <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300 btn-orange-glow">
                        <Icon className="w-8 h-8 text-black" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg md:text-xl font-bold text-white leading-tight truncate">{title}</h3>
                        <p className="text-gray-400 text-sm mt-1">{tagline}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Toggle button */}
                <div className="text-center mt-8">
                  <button
                    onClick={() => setShowAllServices((s) => !s)}
                    data-testid="toggle-all-services-btn"
                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full border-2 border-orange-500/60 text-orange-400 hover:text-black hover:bg-orange-500 font-bold transition-all btn-orange-glow"
                  >
                    {showAllServices ? 'Show Less' : `View All ${services.length} Services`}
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${showAllServices ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </>
            );
          })()}

          {/* Book Now CTA */}
          <div className="text-center mt-12">
            <Button
              onClick={() => window.open('https://form.jotform.com/260199128265058', '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-12 py-6 text-xl rounded-full transform hover:scale-105 transition-all shadow-2xl btn-pulse-glow"
            >
              Book Now
            </Button>
          </div>
        </div>
      </section>

      {/* Web Banner 3 - Building Legacy Through Fashion */}
      {bannerImages[2] && (
        <section className="relative h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={bannerImages[2].media_url}
              alt={bannerImages[2].title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/80"></div>
          </div>
          <div className="relative z-10 container mx-auto h-full flex items-end justify-center px-6 pb-12">
            <div className="text-center">
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-4 heading-oswald heading-glow">
                Building Legacy Through Fashion
              </h3>
              <p className="text-xl text-gray-200">
                Join us in creating pathways for future generations
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Product Categories Section - Tabbed like Hartley */}
      <section id="products" className="py-24 px-6 bg-zinc-900">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">SHOP</span> BY CATEGORY
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
          </div>

          {/* Category Tabs */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            <button
              onClick={() => setActiveTab('jackets')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'jackets'
                  ? 'bg-orange-500 text-black'
                  : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              Jackets
            </button>
            <button
              onClick={() => setActiveTab('hoodies')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'hoodies'
                  ? 'bg-orange-500 text-black'
                  : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              Hoodies
            </button>
            <button
              onClick={() => setActiveTab('tees')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'tees'
                  ? 'bg-orange-500 text-black'
                  : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              T-Shirts
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'all'
                  ? 'bg-orange-500 text-black'
                  : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              View All
            </button>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {allProducts
              .filter((product) => {
                if (activeTab === 'all') return true;
                if (activeTab === 'jackets') return product.category === 'jackets' || product.name.toLowerCase().includes('jacket');
                if (activeTab === 'hoodies') return product.category === 'hoodies' || product.name.toLowerCase().includes('hoodie');
                if (activeTab === 'tees') return product.category === 't-shirts' || product.name.toLowerCase().includes('shirt') || product.name.toLowerCase().includes('tee');
                return true;
              })
              .slice(0, 8)
              .map((product) => (
                <Card
                  key={product.id}
                  className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => window.open(product.shopify_url, '_blank')}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{product.name}</CardTitle>
                    <CardDescription className="text-orange-500 text-2xl font-bold">
                      {product.price}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => window.open(process.env.REACT_APP_SHOPIFY_URL || 'https://shop.finestfeathers.com/collections/all?page=2', '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-12 py-6 text-xl btn-pulse-glow"
            >
              View Full Collection
            </Button>
          </div>
        </div>
      </section>

      {/* NATIVE RIBBON PROMO SECTION */}
      <section className="relative py-20 sm:py-28 overflow-hidden" data-testid="native-ribbon-promo">
        {/* Background with ribbon image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://customer-assets-cm19k8pv.emergentagent.net/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/39307ad3175d2e51_IMG_9896%202.JPG')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/60"></div>
        </div>
        
        {/* Gold accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <p className="text-amber-400 text-sm tracking-[0.3em] mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                FINEST FEATHERS ORIGINAL COLLECTION
              </p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <span className="text-white">NATIVE</span>{' '}
                <span className="text-amber-400">RIBBON</span>
              </h2>
              <p className="text-lg text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0">
                California Native basket-inspired satin ribbon, designed to bring cultural inspiration and creativity into every project. Available in 1.5-inch and 5/8-inch widths.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/native-ribbon">
                  <Button
                    className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-4 text-lg transition-all hover:scale-105 shadow-lg"
                    style={{ boxShadow: '0 0 30px rgba(245, 158, 11, 0.4)' }}
                    data-testid="shop-native-ribbon-btn"
                  >
                    Shop Native Ribbon
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Featured ribbon image */}
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 to-amber-600/10 rounded-2xl blur-2xl"></div>
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/7a05e426bc8aa7aa_IMG_9895.JPG"
                alt="Native Ribbon Collection"
                className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto border border-amber-500/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Web Banner 2 - Amplifying Creative Natives */}
      {bannerImages[1] && (
        <section className="relative h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={bannerImages[1].media_url}
              alt={bannerImages[1].title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
          <div className="relative z-10 container mx-auto h-full flex items-center justify-center px-6 text-center">
            <div className="max-w-3xl">
              <h2 className="text-5xl md:text-7xl font-black text-white mb-6 heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif' }}>
                AMPLIFYING CREATIVE NATIVES
              </h2>
              <p className="text-2xl text-orange-500 font-bold mb-8">
                Fashion • Film • Music
              </p>
              <Button
                onClick={() => window.open('https://www.youtube.com/watch?v=FRgD1qpj77Q', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 flex items-center gap-2 mx-auto btn-orange-glow"
              >
                <ExternalLink className="w-5 h-5" />
                Watch on YouTube
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events Section - removed per user request */}

      {/* Gallery Carousel */}
      <section id="gallery" className="py-24 px-6 bg-gradient-to-b from-black to-zinc-900 ambient-orange-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">GALLERY</span>
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          {/* Carousel */}
          <div className="relative max-w-7xl mx-auto">
            <div className="overflow-hidden rounded-lg">
              <img
                src={pastEventsMedia[currentGalleryIndex]?.media_url}
                alt={pastEventsMedia[currentGalleryIndex]?.title}
                className="w-full h-[600px] object-cover"
              />
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentGalleryIndex((prev) => (prev > 0 ? prev - 1 : pastEventsMedia.length - 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-4 rounded-full transition-all"
            >
              ←
            </button>
            <button
              onClick={() => setCurrentGalleryIndex((prev) => (prev < pastEventsMedia.length - 1 ? prev + 1 : 0))}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-4 rounded-full transition-all"
            >
              →
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {pastEventsMedia.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGalleryIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentGalleryIndex ? 'bg-orange-500 w-8' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Web Banner - Join the Tribe */}
      {bannerImages[0] && (
        <section id="register" className="relative h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={bannerImages[0].media_url}
              alt={bannerImages[0].title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
          </div>
          <div className="relative z-10 container mx-auto h-full flex items-center px-6">
            <div className="max-w-2xl">
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight heading-oswald heading-glow">
                Join the Tribe
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                Be part of amplifying Indigenous voices in fashion, film, and music. Register for upcoming events, workshops, and shows.
              </p>
              <Button
                onClick={() => window.open(NEWSLETTER_FORM_URL, '_blank')}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-6 text-lg btn-pulse-glow"
              >
                Register Now
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Contact Form Section - Drop us a Message */}
      <section id="contact" className="py-24 px-6 bg-zinc-900 ambient-orange-bg">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">DROP US</span> A MESSAGE
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-300">
              Subscribe to our newsletter for all future events, new clothing drops and lots more
            </p>
          </div>

          <div className="bg-black p-8 rounded-lg border border-zinc-800 text-center">
            <p className="text-gray-300 mb-8">Add your name and email to get event announcements, clothing drops and news from Finest Feathers.</p>
            <Button
              data-testid="home-newsletter-signup-btn"
              onClick={() => window.open(NEWSLETTER_FORM_URL, '_blank', 'noopener,noreferrer')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-12 py-6 text-xl rounded-full btn-pulse-glow"
            >
              Sign Up for the Newsletter
            </Button>
          </div>
        </div>
      </section>

      {/* Music Integration Section - Full Width Banner */}
      <section id="music" className="relative h-[700px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/h7gjl7y1_Threads%20of%20Thunder%20Fashion%20Show_Orion%20Sky%20Studios-449.jpg"
            alt="Threads of Thunder Performance"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
        </div>

        <div className="relative z-10 container mx-auto h-full flex items-center justify-center px-6">
          <div className="text-center max-w-4xl">
            <h2 className="text-6xl md:text-8xl font-black text-white mb-6 heading-oswald heading-glow" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.1em' }}>
              LISTEN NOW
            </h2>
            <p className="text-2xl md:text-3xl text-orange-500 font-bold mb-4">
              {musicData?.subtitle || 'New Music Available Now'}
            </p>
            <p className="text-xl text-gray-200 mb-12">
              Stream the sounds amplifying Indigenous voices in music, fashion, and film
            </p>
            
            <div className="flex gap-6 justify-center items-center flex-wrap">
              <Button
                onClick={() => window.open(musicData?.metadata?.apple_music_url || 'https://music.apple.com/us/artist/hands/1810645220', '_blank')}
                className="bg-white hover:bg-gray-200 text-black font-bold px-12 py-8 text-xl flex items-center gap-3 rounded-full shadow-2xl transform hover:scale-105 transition-all btn-orange-glow"
              >
                <Music className="w-6 h-6" />
                Stream on Apple Music
                <ExternalLink className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => window.open(musicData?.metadata?.spotify_url || 'https://open.spotify.com/artist/1XCQTzNK0VRTmoGx6TeQyl', '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white font-bold px-12 py-8 text-xl flex items-center gap-3 rounded-full shadow-2xl transform hover:scale-105 transition-all btn-orange-glow"
              >
                <Music className="w-6 h-6" />
                Stream on Spotify
                <ExternalLink className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
      </section>

      {/* Join EntrepreNatives Section moved up — now appears under Our Mission */}

      {/* Footer */}
      <footer className="bg-black py-12 px-6 border-t border-zinc-800">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Info */}
            <div className="flex flex-col items-center md:items-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/n560cpi8_Untitled%20design%20%2820%29.png" 
                alt="Finest Feathers Logo" 
                className="h-20 w-auto mb-4"
              />
              <h3 className="text-xl font-bold mb-2 text-white" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.14em' }}>
                FINEST FEATHERS
              </h3>
              <p className="text-gray-400 text-center md:text-left mb-4">Amplifying indigenous voices in creative spaces</p>
              <p className="text-gray-400 text-center md:text-left text-sm">Located in Santa Rosa California</p>
              <p className="text-orange-500 text-center md:text-left text-sm font-semibold">Pomo / Karuk</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2">
                <a href="#book-us" className="text-gray-400 hover:text-orange-500 transition-colors">Book Us</a>
                <a href="#contact" className="text-gray-400 hover:text-orange-500 transition-colors">Contact Us</a>
                <a href="#story" className="text-gray-400 hover:text-orange-500 transition-colors">About Us</a>
                <a 
                  href="https://shop.finestfeathers.com/collections/all?page=2" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-orange-500 transition-colors"
                >
                  Shop Now
                </a>
                <a 
                  href="https://www.paypal.com/ncp/payment/M7ZXP8B9GRVSQ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-orange-500 transition-colors"
                >
                  Donate
                </a>
                <a href="#contact" className="text-gray-400 hover:text-orange-500 transition-colors">Join Newsletter</a>
              </div>
            </div>

            {/* Connect - Social Icons */}
            <div>
              <h4 className="text-white font-bold mb-4">Connect</h4>
              <div className="flex flex-wrap gap-3">
                {/* Instagram */}
                <Button
                  onClick={() => window.open('https://www.instagram.com/Finestfeathers', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </Button>
                {/* TikTok */}
                <Button
                  onClick={() => window.open('https://www.tiktok.com/@finestfeathers', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="TikTok"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </Button>
                {/* Email */}
                <Button
                  onClick={() => window.open('mailto:finestfeathersproductions@gmail.com', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="Email Us"
                >
                  <Mail className="w-5 h-5" />
                </Button>
                {/* YouTube */}
                <Button
                  onClick={() => window.open('https://www.youtube.com/@FinestFeathers/videos', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </Button>
                {/* Apple Music */}
                <Button
                  onClick={() => window.open(musicData?.metadata?.apple_music_url || 'https://music.apple.com/', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="Apple Music"
                >
                  <Music className="w-5 h-5" />
                </Button>
                {/* Shop */}
                <Button
                  onClick={() => window.open('https://shop.finestfeathers.com/collections/all?page=2', '_blank')}
                  variant="outline"
                  size="icon"
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  title="Shop"
                >
                  <ShoppingBag className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Additional Info */}
            <div>
              <h4 className="text-white font-bold mb-4">Location</h4>
              <p className="text-gray-400 mb-2">Santa Rosa, California</p>
              <p className="text-orange-500 font-semibold">Pomo / Karuk</p>
            </div>
          </div>
          <div className="text-center text-gray-500 text-sm pt-8 border-t border-zinc-800">
            © 2026 Finest Feathers. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
